/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useU2000GridStateHandlers } from '../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { useU2000SearchStateHandlers } from '../u2000Core/reducer/formReducer/U2000SearchStateHandlers';
import type { U2000GridStateHandlersReturn } from '../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import type { U2000SearchStateHandlersReturn } from '../u2000Core/reducer/formReducer/U2000SearchStateHandlers';
import { useU2000LUStateHandlers } from '../u2000Core/reducer/formReducer/U2000LUHandlers';
import type { U2000LUState } from '../u2000Core/reducer/formReducer/U2000LUState';
import type { U3154InitLU, U3154PaymentTypeSearchRow } from './U3154Service';

export type InitLU = U2000LUState['personalization'] &
  U3154InitLU & {
    launchSearch: boolean;
    hideSearch: boolean;
    nbRecordPerPage: number;
  };

export interface U3154ContextType {
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3154PaymentTypeSearchRow>;
  initLU: U3154InitLU;
}

interface U3154ContextProviderProps {
  initLU: U3154InitLU;
  children: ReactNode;
}

const U3154Context = React.createContext<U3154ContextType | undefined>(undefined);

export const U3154ContextProvider: React.FC<U3154ContextProviderProps> = ({ initLU, children }) => {
  const U2000LUStateHandlers = useU2000LUStateHandlers({ initLU: initLU as InitLU & U2000LUState });
  const U2000SearchStateHandlers = useU2000SearchStateHandlers({
    luName: 'u3154',
    launchSearch: initLU.launchSearch,
    hideSearch: initLU.hideSearch,
  });
  const U2000GridStateHandlers = useU2000GridStateHandlers<U3154PaymentTypeSearchRow>({
    nbRecordPerPage: initLU.nbRecordPerPage ?? 50,
  });

  const contextValue = useMemo<U3154ContextType>(
    () => ({
      luStateHandlers: U2000LUStateHandlers,
      searchStateHandlers: U2000SearchStateHandlers,
      gridStateHandlers: U2000GridStateHandlers,
      initLU,
    }),
    [U2000GridStateHandlers, U2000SearchStateHandlers, U2000LUStateHandlers, initLU],
  );

  return <U3154Context.Provider value={contextValue}>{children}</U3154Context.Provider>;
};

export function useU3154Context(): U3154ContextType {
  const context = useContext(U3154Context);
  if (!context) {
    throw new Error('useU3154Context must be used within a U3154ContextProvider');
  }
  return context;
}
